import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { useModal } from 'store/modal';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { FaPen } from 'react-icons/fa';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity } from 'utils/helpers/client';
import { formatDate } from 'utils/helpers/formatters';
import setServerErrors from 'utils/helpers/formErrors';
import { multiSignDocAsEmployer, signDocAsEmployer } from 'routes/client/documents/helpers';
import { IInputs } from 'routes/client/structure/employees/components/types';
import EmployeeRejectLeave from 'routes/client/leaves/components/EmployeeRejectLeave';
import EmployerRejectLeave from 'routes/client/leaves/components/EmployerRejectLeave';
import Button from 'components/Button';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import { useWorkModeContext } from 'store/work-mode';
import handleEmployeeCode from 'utils/helpers/handleEmployeeCode';
import { HandleEmployeeSignOptions } from 'components/form/CodeForm';
import RejectLeave from 'routes/client/leaves/components/RejectLeave';

interface IConfirmButtonProps {
  employeeRejectionRequestId: number;
  documentHolderId: number;
}

interface IProps {
  data: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  className?: string;
}
const DocsTable: FC<IProps> = ({ data, onMouseEnter, onMouseLeave, className }: IProps) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const { client } = useActiveClientContext();
  const [index, setIndex] = useState<number | null>(null);
  const [isApproving, setIsAproving] = useState(false);
  const [isApprovingAndSigning, setIsApprovingAndSigning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSigning, setIsSigning] = useState(false);

  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isLoadingReject, setIsLoadingReject] = useState(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [isLoadingRejectRequest, setIsLoadingRejectRequest] = useState(false);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { workMode } = useWorkModeContext();
  const isEmployee = workMode === 'employee';

  const statuses: any = {
    initiated: t('leaves.initiated'),
    pending: t('leaves.pending'),
    signing: t('leaves.signing'),
    confirmed: t('leaves.confirmed'),
    approved: t('leaves.approved'),
    rejected: t('leaves.rejected'),
    cancelled: t('leaves.cancelled'),
    employeeRejectInitiated: t('leaves.confirmed'),
  };

  const formatDaysCount = (daysCount: string): string => {
    const parsed = parseFloat(daysCount);
    return parsed % 1 === 0 ? parsed.toFixed(0) : parsed.toFixed(1);
  };

  const approveLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(() => {
        queryClient.resetQueries(buildClientEntity('leaves', client));
        showModal({
          type: 'success',
          heading: t('common.success'),
          content: t('leaves.successfullyApproved'),
        });
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const signLeave = (leaveId: number, leaveDetails: any) => {
    const {
      has_enough_leave_days: hasEnoughLeaveDays,
      type,
      sub_type: subType,
      days_remaining_for_user: remainingDays,
      leave_days: paidDaysCount,
    } = leaveDetails;

    const formattedPaidDaysCount = formatDaysCount(paidDaysCount);

    if (!hasEnoughLeaveDays && type === 'paid' && subType === 'paid_ordinary') {
      showModal({
        type: 'confirm',
        heading: t('leaves.confirmNotEnoughDaysHeading'),
        content: t('leaves.confirmNotEnoughDaysMsg', {
          paidDaysCount: formattedPaidDaysCount,
          remainingDays,
        }),
        onConfirm: () => {
          setIsSigning(true);
          API.get(`leaves/${leaveId}/documents-holders`)
            .then(res => res.data)
            .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
              multiSignDocAsEmployer(
                [reqDocHolderId, orderDocHolderId],
                showModal,
                () => queryClient.resetQueries(buildClientEntity('leaves', client)),
                '',
                t
              ).then(() => {
                setIndex(null);
                setIsSigning(false);
              });
            })
            .catch(err =>
              showModal({
                type: 'error',
                content: createErrorFromResponse(err).message,
              })
            );
        },
      });
      return;
    }

    setIsSigning(true);
    API.get(`leaves/${leaveId}/documents-holders`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        ).then(() => {
          setIndex(null);
          setIsSigning(false);
        });
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const approveAndSignLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        );
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const ConfirmEmployeeRejectRequestButton: FC<IConfirmButtonProps> = ({
    employeeRejectionRequestId,
    documentHolderId,
  }: IConfirmButtonProps) => {
    const [isLoadingConfirmReject, setIsLoadingConfirmReject] = useState(false);
    return (
      <Button
        key={5}
        type="button"
        btnType="info"
        loading={isLoadingConfirmReject}
        onClick={() => {
          if (isLoadingConfirmReject) {
            return;
          }

          setIsLoadingConfirmReject(true);

          API.post(`documents/${employeeRejectionRequestId}/approve`)
            .then(res => {
              signDocAsEmployer(
                employeeRejectionRequestId.toString(),
                documentHolderId.toString(),
                showModal,
                hideModal,
                () => queryClient.invalidateQueries(buildClientEntity('leaves', client)),
                'documents.table.approvedAndSignedDocSuccessMsg',
                t
              );
            })
            .catch(err => {
              signDocAsEmployer(
                employeeRejectionRequestId.toString(),
                documentHolderId.toString(),
                showModal,
                hideModal,
                () => queryClient.invalidateQueries(buildClientEntity('leaves', client)),
                'documents.table.approvedAndSignedDocSuccessMsg',
                t
              );
            });
        }}
      >
        {t('leaves.confirmEmployeeRejection')}
      </Button>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleEmployeeLeaveSign = (options: HandleEmployeeSignOptions) => {
    // Sign process used in "employee" mode.
    // AdES (Advanced Electronic Signature)
    //
    // The process takes place with one request to BE,
    // as the signing is done with self-signed certificates,
    // which are kept on the backend.
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { docId, data, methodsProp } = options;
    return API.post(`documents/${docId}/sign-ades`, data)
      .then(res => {
        showModal({
          type: 'success',
          content: <>{t('leaves.createSuccessMsg')}.</>,
        });
        return queryClient.invalidateQueries('my-leaves');
      })
      .catch(response => {
        const error = createErrorFromResponse(response);
        if (methodsProp) {
          setServerErrors(error.errors, methodsProp.setError);
        } else {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
        }
      });
  };

  const handleAttachmentClick = (id: number) => {
    return API.get(`leaves/attachment/${id}/download`)
      .then(res => {
        window.open(res.data, '_blank');
      })
      .catch(res => {
        const error = createErrorFromResponse(res);
        showModal({
          type: 'error',
          content: error.message,
        });
      });
  };

  const handleMoreDetails = (modalData: any) => {
    const modalContent = (
      <div className="d-flex flex-column align-items-start">
        <h3 className="text-center mb-3 w-100">
          Детайли за отпуск {formatDate(new Date(modalData.date_from), 'dd.mm.YYY')} -{' '}
          {formatDate(new Date(modalData.date_to), 'dd.mm.YYY')}
        </h3>
        <p
          className="mb-1"
          style={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
          <b>{t('leaves.type')}:</b> {modalData.type_label} <b>{t('leaves.numberOfDays')}:</b> {modalData.leave_days}{' '}
          <b>{t('leaves.status')}:</b> {statuses[modalData.status as string]}
        </p>
        {modalData.comment && (
          <p className="mb-1">
            <b>{t('leaves.comment')}:</b> {modalData.comment}
          </p>
        )}
        <div
          className="d-flex flex-column mt-3 w-100"
          style={{ gap: '10px' }}
        >
          <div
            className="d-flex flex-wrap justify-content-center align-items-center w-100"
            style={{ gap: '10px' }}
          >
            {modalData.buttons.map((button: { type: string; text: string; action_link?: any }, i: any) => {
              const { type, text } = button;
              if (type === 'employee_reject') {
                return (
                  <EmployeeRejectLeave
                    key={`employee-reject-leave-${type}-${text}`}
                    leaveId={modalData.id}
                    buttonText={text}
                    isMobile
                    invalidate={() => queryClient.invalidateQueries('my-leaves')}
                  />
                );
              }
              if (type === 'employee_reject_without_document') {
                return (
                  <EmployeeRejectLeave
                    key={`employee-reject-leave-${type}-${text}`}
                    leaveId={modalData.id}
                    buttonText={text}
                    isMobile
                    invalidate={() => queryClient.invalidateQueries('my-leaves')}
                    isWithoutDocument
                  />
                );
              }
              if (type === 'employeer_deny_reject')
                return (
                  <div
                    className="d-inline-block"
                    key={`employee-reject-leave-${type}-${text}`}
                  >
                    <Button
                      type="button"
                      btnType="danger"
                      loading={isLoading && modalData.id === index}
                      onClick={() => {
                        setIndex(modalData.id);
                        setIsLoading(true);
                        API.patch(button.action_link)
                          .then(res => {
                            setIsLoading(false);
                            setIndex(null);
                            hideModal();
                            showModal({
                              type: 'success',
                              content: t('common.success'),
                            });
                            queryClient.resetQueries(buildClientEntity('leaves', client));
                          })
                          .catch(err =>
                            showModal({
                              type: 'error',
                              content: createErrorFromResponse(err).message,
                            })
                          );
                      }}
                    >
                      {button.text}
                    </Button>
                  </div>
                );
              if (type === 'employer_reject')
                return (
                  <NegativeMarginleft>
                    <EmployerRejectLeave
                      key={modalData.id}
                      leaveId={modalData.id}
                      buttonText={text}
                      handlePreview={() =>
                        handlePreviewDoc({
                          showModal,
                          id: modalData.request_document_id,
                        })
                      }
                      invalidate={() => queryClient.invalidateQueries(buildClientEntity('leaves', client))}
                    />
                  </NegativeMarginleft>
                );
              if (type === 'reject')
                return (
                  <RejectLeave
                    key={modalData.id}
                    leaveId={modalData.id}
                    buttonText={text}
                    buttonInnerText={text}
                    buttonSize={null}
                    handlePreview={() => handlePreviewDoc({ showModal, id: modalData.id })}
                    invalidate={() => queryClient.invalidateQueries(buildClientEntity('leaves', client))}
                    noTooltip
                  />
                );
              if (type === 'sign')
                return (
                  <div
                    key={i}
                    className="d-inline-block"
                  >
                    <Button
                      key={modalData.id}
                      type="button"
                      btnType="info"
                      disabled={isSigning && modalData.id === index}
                      loading={isSigning && modalData.id === index}
                      onClick={() => {
                        setIndex(modalData.id);
                        signLeave(modalData.id, modalData);
                      }}
                    >
                      {button.text}
                    </Button>
                  </div>
                );
              if (type === 'approve_and_sign')
                return (
                  <div
                    key={i}
                    className="d-inline-block"
                  >
                    <Button
                      key={modalData.id}
                      type="button"
                      btnType="info"
                      disabled={isApprovingAndSigning && modalData.id === index}
                      loading={isApprovingAndSigning && modalData.id === index}
                      onClick={() => {
                        setIndex(modalData.id);
                        setIsApprovingAndSigning(true);
                        approveAndSignLeave(modalData.id).then(() => {
                          setIndex(null);
                          setIsApprovingAndSigning(false);
                        });
                      }}
                    >
                      {button.text}
                    </Button>
                  </div>
                );
              if (type === 'approve')
                return (
                  <div
                    key={i}
                    className="d-inline-block"
                  >
                    <Button
                      key={modalData.id}
                      type="button"
                      btnType="success"
                      disabled={isApproving && modalData.id === index}
                      loading={isApproving && modalData.id === index}
                      onClick={() => {
                        setIndex(modalData.id);
                        setIsAproving(true);
                        approveLeave(modalData.id).then(() => {
                          setIndex(null);
                          setIsAproving(false);
                        });
                      }}
                    >
                      {button.text}
                    </Button>
                  </div>
                );
              if (type === 'employeer_confirm_reject') {
                return (
                  <div
                    key={i}
                    className="d-inline-block ml-2"
                  >
                    <Button
                      key={modalData.id}
                      type="button"
                      btnType="info"
                      onClick={() => {
                        showModal({
                          type: 'form',
                          heading: t('documents.table.confirmDocHeading'),
                          content: (
                            <>
                              <p>{t('leaves.employer.confirmLeaveRejectRequestMsg')}</p>
                              <hr />
                              <ConfirmEmployeeRejectRequestButton
                                employeeRejectionRequestId={modalData.reject_request_document.id}
                                documentHolderId={modalData.reject_request_document.document_holder_id}
                              />
                            </>
                          ),
                        });
                      }}
                    >
                      {button.text}
                    </Button>
                  </div>
                );
              }
              if (type === 'edit') {
                return (
                  <div
                    key={i}
                    className="d-inline-block"
                  >
                    <Button
                      key={modalData.id}
                      type="button"
                      btnType="primary"
                      outlined
                      onClick={() => {
                        hideModal();
                        history.push(`/leaves/manually/edit/${modalData.id}`);
                      }}
                    >
                      {button.text}
                    </Button>
                  </div>
                );
              }
              return null;
            })}
            {modalData.status === 'initiated' && (
              <Button
                type="button"
                btnType="info"
                onClick={() => {
                  handleEmployeeCode({
                    docId: modalData.request_document_id,
                    showModal,
                    heading: t('documents.table.signWithCodeLabel'),
                    handleEmployeeSign: handleEmployeeLeaveSign,
                  });
                  queryClient.invalidateQueries('my-leaves');
                }}
              >
                <FaPen
                  size={14}
                  className="mr-2"
                />
                {t('leaves.table.sign')}
              </Button>
            )}
          </div>
          <div
            className="d-flex flex-wrap justify-content-center align-items-center w-100"
            style={{ gap: '10px' }}
          >
            {modalData.request_document_id && (
              <Button
                type="button"
                btnType="warning"
                outlined
                disabled={isLoadingRequest}
                onClick={() =>
                  handlePreviewDoc({ showModal, id: modalData.request_document_id, setIsLoading: setIsLoadingRequest })
                }
              >
                {t('leaves.requestDocPreview')}
              </Button>
            )}
            {modalData.order_document_id && (
              <Button
                type="button"
                btnType="warning"
                outlined
                disabled={isLoadingOrder}
                onClick={() =>
                  handlePreviewDoc({ showModal, id: modalData.order_document_id, setIsLoading: setIsLoadingOrder })
                }
              >
                {t('leaves.orderDocPreview')}
              </Button>
            )}
            {modalData.reject_document_id && modalData.status === 'rejected' && (
              <Button
                type="button"
                btnType="warning"
                outlined
                disabled={isLoadingReject}
                onClick={() =>
                  handlePreviewDoc({ showModal, id: modalData.reject_document_id, setIsLoading: setIsLoadingReject })
                }
              >
                {t('leaves.orderDocPreview')}
              </Button>
            )}
            {modalData.reject_request_document_id && modalData.status === 'rejected' && (
              <Button
                type="button"
                btnType="warning"
                outlined
                disabled={isLoadingRejectRequest}
                onClick={() =>
                  handlePreviewDoc({
                    showModal,
                    id: modalData.reject_request_document_id,
                    setIsLoading: setIsLoadingRejectRequest,
                  })
                }
              >
                {t('leaves.rejectionRequestDocPreview')}
              </Button>
            )}
          </div>
        </div>

        {modalData.attachments.length > 0 && (
          <>
            <h4 className="text-center mt-3 w-100">{t('leaves.attachmentsList')}:</h4>
            {modalData.attachments.map((attachment: any) => (
              <div
                key={attachment.id}
                className="d-flex flex-column mt-2 w-100"
                onClick={() => handleAttachmentClick(attachment.id)}
              >
                <h5 style={{ cursor: 'pointer' }}>
                  <u>
                    {attachment.file_name}.{attachment.extension}
                  </u>
                </h5>
              </div>
            ))}
          </>
        )}
      </div>
    );

    showModal({
      type: 'info',
      content: modalContent,
    });
  };

  return (
    <td
      colSpan={100}
      style={{ borderTop: 'none', paddingTop: 0 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={className}
    >
      <div className="w-100 d-flex justify-content-between align-items-center">
        <div className="pr-2">
          {data?.user_name && !isEmployee && <strong className="d-block">{data.user_name}</strong>}

          <span>
            <b>{t('leaves.status')}:</b> {statuses[data.status as string]}
          </span>
        </div>
        <div className="mt-auto">
          {data.status === 'initiated' && (
            <Button
              className="mr-2"
              type="button"
              btnType="info"
              onClick={() => {
                handleEmployeeCode({
                  docId: data.request_document_id,
                  showModal,
                  heading: t('documents.table.signWithCodeLabel'),
                  handleEmployeeSign: handleEmployeeLeaveSign,
                });
                queryClient.invalidateQueries('my-leaves');
              }}
            >
              <FaPen size={14} />
            </Button>
          )}
          <Button
            ariaLabel="Leaves Details"
            type="button"
            btnType="primary"
            onClick={() => handleMoreDetails(data)}
          >
            {t('leaves.details')}
          </Button>
        </div>
      </div>
    </td>
  );
};

export const NegativeMarginleft = styled.div`
  margin-left: -0.5rem;
`;

export default DocsTable;
